import React, { useState, useEffect, useCallback } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { cryptoApi } from '../utils/api';
import { useLanguage } from '../i18n/LanguageContext';
import CurrencyIcon from './CurrencyIcon';

const TIMEFRAMES = [
  { label: '24H', value: '1' },
  { label: '7J', value: '7' },
  { label: '30J', value: '30' },
  { label: '90J', value: '90' },
];

const PriceChart = ({ symbol = 'BTC', currentPrice = 0, change24h = 0 }) => {
  const { t } = useLanguage();
  const [timeframe, setTimeframe] = useState('7');
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchChartData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await cryptoApi.getChart(symbol, timeframe);
      const prices = res.data?.prices || [];
      setChartData(prices.map(([timestamp, price]) => ({
        timestamp, price,
        date: new Date(timestamp).toLocaleDateString('fr-FR', {
          day: '2-digit', month: 'short',
          hour: timeframe === '1' ? '2-digit' : undefined,
          minute: timeframe === '1' ? '2-digit' : undefined,
        }),
      })));
    } catch {
      setChartData([]);
    } finally { setLoading(false); }
  }, [symbol, timeframe]);

  useEffect(() => { fetchChartData(); }, [fetchChartData]);

  const formatPrice = (price) => {
    if (price >= 1000) return `$${price.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}`;
    if (price >= 1) return `$${price.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}`;
    return `$${price.toLocaleString('fr-FR', { maximumFractionDigits: 6 })}`;
  };

  const minPrice = chartData.length > 0 ? Math.min(...chartData.map(d => d.price)) : 0;
  const maxPrice = chartData.length > 0 ? Math.max(...chartData.map(d => d.price)) : 0;
  const priceChange = chartData.length > 1 ? ((chartData[chartData.length - 1].price - chartData[0].price) / chartData[0].price) * 100 : 0;
  const isPositive = priceChange >= 0;
  const displayPrice = currentPrice || (chartData.length ? chartData[chartData.length - 1].price : 0);

  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-6" data-testid="price-chart">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center space-x-2">
            <CurrencyIcon symbol={symbol} size={28} />
            <h3 className="text-xl font-extrabold text-gray-900 tracking-tight">{symbol}/USD</h3>
          </div>
          <div className="flex items-center space-x-3 mt-1">
            <span className="text-2xl font-bold text-gray-900 font-mono-data" data-testid="chart-current-price">{formatPrice(displayPrice)}</span>
            <span className={`flex items-center text-sm font-medium font-mono-data ${isPositive ? 'text-emerald-500' : 'text-rose-500'}`}>
              {isPositive ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
              {isPositive ? '+' : ''}{priceChange.toFixed(2)}%
            </span>
          </div>
        </div>
        <div className="flex items-center space-x-1 bg-gray-50 p-1 rounded-full border border-gray-100">
          {TIMEFRAMES.map((tf) => (
            <button key={tf.value} onClick={() => setTimeframe(tf.value)}
              data-testid={`timeframe-${tf.label}`}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                timeframe === tf.value ? 'bg-[#191C1F] text-white' : 'text-gray-500 hover:text-gray-900'
              }`}>{tf.label}</button>
          ))}
        </div>
      </div>

      <div className="h-[300px]">
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-[#191C1F] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={isPositive ? "#10B981" : "#F43F5E"} stopOpacity={0.2}/>
                  <stop offset="95%" stopColor={isPositive ? "#10B981" : "#F43F5E"} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F2F4" />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#9CA3AF' }} tickLine={false} axisLine={false} />
              <YAxis domain={[minPrice * 0.99, maxPrice * 1.01]} tick={{ fontSize: 12, fill: '#9CA3AF' }} tickLine={false} axisLine={false} tickFormatter={(v) => formatPrice(v)} width={80} />
              <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #E5E7EB', borderRadius: '16px', color: '#191C1F' }}
                labelStyle={{ color: '#9CA3AF', fontWeight: 500 }} formatter={(v) => [formatPrice(v), t.common?.price || 'Prix']} />
              <Area type="monotone" dataKey="price" stroke={isPositive ? "#10B981" : "#F43F5E"} strokeWidth={2} fill="url(#colorPrice)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-100">
        <div><div className="text-sm text-gray-400">{t.common?.high || 'Plus haut'}</div><div className="font-semibold text-gray-900 font-mono-data">{formatPrice(maxPrice)}</div></div>
        <div><div className="text-sm text-gray-400">{t.common?.low || 'Plus bas'}</div><div className="font-semibold text-gray-900 font-mono-data">{formatPrice(minPrice)}</div></div>
        <div><div className="text-sm text-gray-400">{t.common?.variation || 'Variation'}</div><div className={`font-semibold font-mono-data ${isPositive ? 'text-emerald-500' : 'text-rose-500'}`}>{isPositive ? '+' : ''}{priceChange.toFixed(2)}%</div></div>
      </div>
    </div>
  );
};

export default PriceChart;
